import FormErrorSummaryLink from "@JohnLewisPartnership/checkout-forms/dist/components/form-error-summary-link/FormErrorSummaryLink";
import FormField from "@JohnLewisPartnership/checkout-forms/dist/components/form-field/FormField";
import { Message } from "@JohnLewisPartnership/jl-design-system/dist/components/message/Message";
import { PrimaryButton } from "@JohnLewisPartnership/jl-design-system/dist/elements/button/Button";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styles from "./styles.module.scss";

const getErrorContent = (errors, showNetworkError, serverError, hideFirstErrorLink) => {
  if (showNetworkError) {
    return { title: "We're sorry, but something's not working right now", body: "Please try again later" };
  }
  if (serverError) {
    return serverError;
  }
  return {
    title:
      errors === 1
        ? "There is 1 error above. Please correct it and try again."
        : `There are ${errors} errors above. Please correct them and try again.`,
    body: <FormErrorSummaryLink hideFirstErrorLink={hideFirstErrorLink} totalValidationErrors={errors} />,
  };
};

const validateField = (id, value, validationConfig) => {
  const { required, regex, maxLength, minLength, validationMessageKeys, attachmentsLength } = validationConfig[id];
  let errorMessage = "";

  if (required && attachmentsLength !== undefined && attachmentsLength < 1) {
    errorMessage = validationMessageKeys.required;
  } else if (required && value === "") {
    errorMessage = validationMessageKeys.required;
  } else if (value && maxLength && value.length > maxLength) {
    errorMessage = validationMessageKeys.maxLength;
  } else if (value && minLength && value.length < minLength) {
    errorMessage = validationMessageKeys.minLength;
  } else if (value && regex) {
    errorMessage = validateRegex(id, regex, value, validationMessageKeys);
  }

  return errorMessage;
};

const validateRegex = (id, regex, value, validationMessageKeys) => {
  let errorMessage = "";

  if (regex instanceof RegExp) {
    if (!value.match(regex)) {
      errorMessage = validationMessageKeys.regex;
    }
  } else {
    regex.forEach(rgx => {
      const { message = "", pattern, getStringSegment, positiveMatch = true } = rgx;

      if (pattern instanceof RegExp) {
        if (typeof getStringSegment === "function") {
          const stringSegment = getStringSegment(value);
          if (typeof stringSegment === "string") {
            const doesMatch = pattern.test(stringSegment);
            if ((positiveMatch && doesMatch) || (!positiveMatch && !doesMatch)) {
              errorMessage = validationMessageKeys[message];
            }
          }
        } else if (!pattern.test(value) && !errorMessage) {
          errorMessage = validationMessageKeys[message];
        }
      }
    });
  }
  return errorMessage;
};

const Form = ({
  fields: fieldDefinitions,
  initialValues = {},
  onSubmit,
  showNetworkError,
  serverError,
  formId,
  hideFirstErrorLink,
  showButton = false,
  errorsCallback = () => {},
}) => {
  const [fields, setFields] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const validationConfig = useRef({});
  const stableFieldDefinitions = useMemo(() => fieldDefinitions, [fieldDefinitions]);

  useEffect(() => {
    if (!showButton && submitted) {
      setSubmitted(false);
    }
  }, [showButton]);

  useEffect(() => {
    const initFields = {};

    stableFieldDefinitions.forEach(field => {
      const { validationMessageKeys, maxLength, minLength, regex, required, attachmentsLength } = field;

      validationConfig.current[field.id] = {
        validationMessageKeys,
        maxLength,
        minLength,
        regex,
        required,
        attachmentsLength,
      };
      initFields[field.id] = {
        name: field.name,
        value: initialValues[field.name] || "",
        error: submitted ? validateField(field.id, initialValues[field.name], validationConfig.current) : "",
      };
    });
    setFields(initFields);
  }, [stableFieldDefinitions, submitted]);

  const onFieldChange = useCallback(
    (id, e) => {
      const value = e.target ? e.target.value : e;

      setFields(prevFields => {
        const updatedFields = { ...prevFields };
        updatedFields[id] = {
          ...updatedFields[id],
          value,
          error: submitted ? validateField(id, value, validationConfig.current) : updatedFields[id].error,
        };

        return updatedFields;
      });
    },
    [validateField, submitted],
  );

  const onSubmitHandle = e => {
    e.preventDefault();
    setSubmitted(true);
    const formData = {};
    const errors = {};
    let valid = true;

    const updatedFields = Object.keys(fields).reduce((acc, fieldId) => {
      const field = fields[fieldId];
      const error = validateField(fieldId, field.value, validationConfig.current);

      formData[field.name] = field.value;

      if (error) {
        errors[field.name] = error;
        valid = false;
      }
      acc[fieldId] = { ...field, error };
      return acc;
    }, {});
    setFields(updatedFields);

    if (!valid) {
      errorsCallback(errors);
    }

    if (valid && typeof onSubmit === "function") {
      setSubmitting(true);
      onSubmit(fields, () => {
        setSubmitting(false);
        setSubmitted(false);
      });
    }
  };

  const countErrors = () => {
    return Object.keys(fields).reduce((acc, id) => {
      const field = fields[id];
      return field?.error ? acc + 1 : acc;
    }, 0);
  };

  const errors = countErrors();
  const submitFailed = errors > 0 || showNetworkError || serverError;
  const { title: errorTitle, body: errorBody } = getErrorContent(
    errors,
    showNetworkError,
    serverError,
    hideFirstErrorLink,
  );

  return (
    <form className={styles["c-form"]} onSubmit={onSubmitHandle} noValidate data-jlds={true}>
      {fieldDefinitions.map(field => {
        const fieldState = fields[field.id] || {};
        const { onChange, input, ...allOtherProps } = field;

        return (
          <FormField
            key={field.key}
            formId={formId}
            meta={{
              error: fieldState.error,
              submitFailed,
            }}
            input={{
              ...input,
              value: fieldState.value,
              onBlur: () => {},
              onFocus: () => {},
              name: field.id,
              onChange: e => {
                onFieldChange(field.id, e);
                if (onChange) {
                  onChange(e);
                }
              },
            }}
            highlightError={!!fieldState.error}
            element={field.component}
            {...allOtherProps}
          />
        );
      })}

      {submitFailed && (
        <div className={styles["c-form__error"]}>
          <Message
            dataId={`${formId}-error-message`}
            title={errorTitle}
            body={errorBody}
            type="error"
            className={styles.message}
            scrollToMessageOnMount={true}
            role="alert"
            wideTab={true}
            whiteBackground={true}
          />
        </div>
      )}

      {showButton ? (
        <div className={styles["c-form__submit"]}>
          <PrimaryButton type="submit" submitting={submitting}>
            Submit
          </PrimaryButton>
        </div>
      ) : null}
    </form>
  );
};

export default Form;
